// Utilities to generate a circular logo (with orange border) for embedding in QR codes.
// We build a data URL using an offscreen canvas so the QR renderer can embed it via `imageSettings.src`.

const DEFAULT_BORDER_COLOR = '#f97316'; // orange-500

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Attempt CORS-safe loading when src is remote.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/**
 * Builds a data URL containing the provided logo centered inside a white circle with an orange border.
 * If the logo cannot be loaded (e.g. CORS), we return the original `logoSrc` so QR still renders.
 */
export async function buildCircledLogoDataUrl({
  logoSrc,
  sizePx = 128,
  borderColor = DEFAULT_BORDER_COLOR,
  borderWidthPx = 10,
  backgroundColor = '#ffffff',
  paddingPx = 12,
} = {}) {
  if (!logoSrc) return null;

  try {
    const img = await loadImage(logoSrc);

    const canvas = document.createElement('canvas');
    canvas.width = sizePx;
    canvas.height = sizePx;

    const ctx = canvas.getContext('2d');
    if (!ctx) return logoSrc;

    const center = sizePx / 2;
    const outerRadius = center - 1;

    // Background circle
    ctx.save();
    ctx.beginPath();
    ctx.arc(center, center, outerRadius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = backgroundColor;
    ctx.fill();

    // Border
    ctx.lineWidth = borderWidthPx;
    ctx.strokeStyle = borderColor;
    ctx.stroke();
    ctx.restore();

    // Clip inner circle to place logo
    const innerRadius = outerRadius - borderWidthPx / 2 - paddingPx;

    ctx.save();
    ctx.beginPath();
    ctx.arc(center, center, Math.max(1, innerRadius), 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    // Fit logo inside the clipped circle (contain)
    const maxW = innerRadius * 2;
    const maxH = innerRadius * 2;

    const scale = Math.min(maxW / img.width, maxH / img.height);
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const x = center - drawW / 2;
    const y = center - drawH / 2;

    ctx.drawImage(img, x, y, drawW, drawH);
    ctx.restore();

    return canvas.toDataURL('image/png');
  } catch (e) {
    // Most common: remote logo blocks CORS. Fall back to original src.
    return logoSrc;
  }
}
